import { MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, MenuItem, Switch, Tooltip } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { WorkflowRuleDto, WorkflowRulePostRequestDto } from 'src/generated/services/TFinancialApi';
import { useEnhancedTable } from 'src/presentations/hooks/useEnhancedTable';
import { useTableEntityHandler } from 'src/presentations/hooks/useTableEntityHandler';
import { SettingsBaseTable } from 'src/presentations/components/molecules/settings/table/BaseTable';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';
import { useUpsertWorkflowRule, useDeleteWorkflowRule } from 'src/use-cases/settings/useWorkflowRules';
import { WorkflowRuleDrawer } from './WorkflowRuleDrawer';

interface AuditWorkflowTableProps {
  workflowId?: string;
  workflowRules: WorkflowRuleDto[];
  isLoading: boolean;
  onRefetch: () => void;
  onTableReady?: (table: any) => void;
}

export const AuditWorkflowTable: FC<AuditWorkflowTableProps> = (props): JSX.Element => {
  const { workflowId, workflowRules, isLoading, onRefetch, onTableReady } = props;
  const [rowToDelete, setRowToDelete] = useState<WorkflowRuleDto | null>(null);
  const [selectedRule, setSelectedRule] = useState<WorkflowRuleDto | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { pagination, sorting, handleSortingChange, onChangePage, onChangePageSize, getTextFieldProps, validateItem, clearValidationErrors } =
    useEnhancedTable<WorkflowRuleDto>({
      initialSorting: [
        {
          id: 'id',
          desc: false,
        },
      ],
      requiredFields: ['description', 'type', 'workflowAction'],
    });

  const { mutateAsync: upsertWorkflowRule } = useUpsertWorkflowRule();
  const { mutateAsync: deleteWorkflowRule } = useDeleteWorkflowRule();

  const columns = useMemo(
    (): MRT_ColumnDef<WorkflowRuleDto>[] => [
      {
        accessorKey: 'userRoles',
        header: 'User Roles',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const roles = cell.getValue<string[]>();
          if (!roles || roles.length === 0) return '-';

          const roleLabels = {
            ADMIN: 'Admin',
            'OPERATIONS-MANAGER': 'Operation',
            'PROCUREMENT-MANAGER': 'Procurement',
            FINANCE: 'Finance',
            'SUPPLY-CHAIN': 'Supply Chain',
          };

          return (
            <div className='flex flex-wrap gap-1 '>
              {roles.map((role, index) => (
                <span key={role}>
                  <span>{roleLabels[role] || role}</span>
                  {index < roles.length - 1 && <span className='px-1'>,&nbsp;</span>}
                </span>
              ))}
            </div>
          );
        },
        muiEditTextFieldProps: ({ row, table }) => {
          const currentValue = row._valuesCache.userRoles ?? row.original.userRoles ?? [];
          // Ensure value is always an array
          const value = Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : [];

          return {
            select: true,
            required: true,
            value,
            onChange: (e) => {
              row._valuesCache.userRoles = e.target.value;
            },
            children: [
              { value: 'ADMIN', label: 'Admin' },
              { value: 'OPERATIONS-MANAGER', label: 'Operation' },
              { value: 'PROCUREMENT-MANAGER', label: 'Procurement' },
              { value: 'FINANCE', label: 'Finance' },
              { value: 'SUPPLY-CHAIN', label: 'Supply Chain' },
            ].map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            )),
            SelectProps: {
              multiple: true,
            },
          };
        },
      },
      {
        accessorKey: 'id',
        header: 'Rule ID',
        enableSorting: true,
        enableColumnFilter: false,
        enableEditing: false,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        enableColumnFilter: false,
        muiEditTextFieldProps: getTextFieldProps('description', { required: true }),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        enableSorting: false,
        enableColumnFilter: false,
        editVariant: 'select',
        editSelectOptions: [
          { value: 'INVOICE', label: 'Invoice' },
          { value: 'SHIPMENT', label: 'Shipment' },
        ],
      },
      {
        accessorKey: 'workflowAction',
        header: 'Workflow Action',
        enableSorting: false,
        enableColumnFilter: false,
        editVariant: 'select',
        editSelectOptions: [
          { value: 'SEND_TO_OPERATION', label: 'Send to Operation' },
          { value: 'SEND_TO_PROCUREMENT', label: 'Send to Procurement' },
          { value: 'SEND_TO_FINANCE', label: 'Send to Finance' },
          { value: 'START_DISPUTE', label: 'Start Dispute' },
          { value: 'CANCEL_DISPUTE', label: 'Cancel Dispute' },
          { value: 'MARK_INVOICE_TO_REJECT', label: 'Mark Invoice to Reject' },
          { value: 'MARK_INVOICE_TO_APPROVE', label: 'Mark Invoice to Approve' },
          { value: 'ACCEPT_CREDIT_NOTE', label: 'Accept Credit Note' },
          { value: 'ACCEPT_APPROVAL', label: 'Accept Approval' },
          { value: 'READY_FOR_PAYMENT', label: 'Ready for Payment' },
          { value: 'REJECT_INVOICE', label: 'Reject Invoice' },
          { value: 'ACCEPT_INVOICE', label: 'Accept Invoice' },
          { value: 'ACCEPT_AS_SPOT_SHIPMENT', label: 'Accept as Spot Shipment' },
        ],
      },
      {
        accessorKey: 'version',
        header: 'Version',
        enableSorting: false,
        enableColumnFilter: false,
        muiEditTextFieldProps: getTextFieldProps('version'),
      },
    ],
    [getTextFieldProps]
  );

  // Function to prepare the entity for upsert operation
  const prepareEntityForUpsert = useCallback(
    (values: Record<string, any>, originalData?: any): WorkflowRulePostRequestDto => {
      const {
        id,
        description,
        type,
        workflowAction,
        enabled,
        currentWorkflow,
        nextWorkflow,
        nextInvoiceStatus,
        nextShipmentStatus,
        userRoles,
        priority,
        version,
        condition,
        postAction,
        activityLogType,
      } = values;

      const dto: WorkflowRulePostRequestDto = {
        id: originalData?.id || id || undefined,
        description: description || '',
        type: type || 'INVOICE',
        workflowAction: workflowAction || 'SEND_TO_OPERATION',
        nextInvoiceStatus: nextInvoiceStatus || 'TO_APPROVE',
        nextShipmentStatus: nextShipmentStatus || 'UNDER_REVIEW',
        enabled: enabled ?? true,
        currentWorkflow: currentWorkflow || undefined,
        nextWorkflow: nextWorkflow || undefined,
        userRoles: userRoles || undefined,
        priority: priority || undefined,
        version: version || undefined,
        condition: condition || undefined,
        postAction: postAction || undefined,
        activityLogType: activityLogType || undefined,
        workflowId: workflowId || undefined, // Add workflowId from props
      };

      return dto;
    },
    [workflowId]
  );

  const {
    handleCreate: handleCreateWorkflowRule,
    handleUpdate: handleUpdateWorkflowRule,
    handleDelete: handleDeleteWorkflowRule,
    isLoading: isLoadingTableEntity,
  } = useTableEntityHandler<WorkflowRulePostRequestDto>(
    {
      entityName: 'Workflow Rule',
      upsertEntity: upsertWorkflowRule,
      deleteEntity: (params: { id: number }) => deleteWorkflowRule(params.id),
      prepareEntityForUpsert,
    },
    validateItem
  );

  const renderRowActions = useCallback(
    ({ row, table }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Switch
          checked={row.original.enabled ?? true}
          onChange={(e) => {
            // Handle toggle
            const updatedRule = { ...row.original, enabled: e.target.checked };
            upsertWorkflowRule(updatedRule).then(() => onRefetch());
          }}
          color='success'
        />
      </Box>
    ),
    [upsertWorkflowRule, onRefetch]
  );

  // Custom cell props to handle row clicks for opening drawer
  const customCellProps = useCallback(
    ({ row, column, table }) => ({
      onClick: () => {
        // Only open drawer if not in edit mode and not clicking on actions column
        const isEditingThisRow = table.getState().editingRow?.id === row.id;
        const isCreatingRow = table.getState().creatingRow !== null;

        if (column?.id !== 'mrt-row-actions' && row?.original?.id !== '' && !isEditingThisRow && !isCreatingRow) {
          setSelectedRule(row.original);
          setIsDrawerOpen(true);
        }
      },
      sx: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          textDecoration: 'underline',
        },
      },
    }),
    []
  );

  const baseTableConfig = createTableConfig<WorkflowRuleDto>({
    hiddenColumns: [],
    onCreatingCancel: clearValidationErrors,
    onEditingCancel: clearValidationErrors,
  });

  const table = useMaterialReactTable({
    ...baseTableConfig,
    onCreatingRowSave: handleCreateWorkflowRule,
    onEditingRowSave: handleUpdateWorkflowRule,
    data: workflowRules,
    columns,
    layoutMode: 'semantic',
    state: {
      sorting,
      isLoading,
    },
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 140,
      },
    },
    muiTableBodyCellProps: customCellProps,
    onSortingChange: (updatedSorting) => {
      if (typeof updatedSorting === 'function') {
        const newSortingState = updatedSorting(sorting);
        handleSortingChange(newSortingState);
      } else {
        handleSortingChange(updatedSorting);
      }
    },
    renderRowActions: ({ row, table }) => renderRowActions({ row, table }),
  });

  // Notify parent when table is ready
  useEffect(() => {
    if (onTableReady) {
      onTableReady(table);
    }
  }, [table, onTableReady]);

  return (
    <div className='flex flex-col flex-1 min-h-0'>
      <div className='w-full overflow-x-auto'>
        <div className='w-auto min-w-full'>
          <SettingsBaseTable
            table={table}
            totalItems={workflowRules.length}
            pagination={pagination}
            onChangePage={onChangePage}
            onChangePageSize={onChangePageSize}
          />
        </div>
      </div>

      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isLoadingTableEntity}
        onAccept={async () => {
          if (rowToDelete?.id) {
            await handleDeleteWorkflowRule({ original: rowToDelete });
            setRowToDelete(null);
          }
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure want to delete this workflow rule?</p>
      </ModalConfirm>

      <WorkflowRuleDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedRule(null);
        }}
        workflowRule={selectedRule}
        onSuccess={onRefetch}
      />
    </div>
  );
};
